import projectValue from '#calculator/projectValue'
import type { TaxCalculationResult } from '#calculator/types'
import { TaxKey, type TaxMetadata } from '#dictionary'
import { FilingStatus } from '#dictionary/filingStatuses'
import { TAX_BY_KEY } from '#dictionary/taxes'
import {
  CAPITAL_GAINS_BRACKETS,
  FEDERAL_INCOME_BRACKETS,
  type TaxFederalBracket,
} from '#dictionary/taxFederalBrackets'

import type {
  ITaxCalculator,
  TaxBracketBreakdown,
  TaxVarietyWithFilingStatus,
} from './types'

type FederalTaxKey = TaxKey.TaxFederalIncome | TaxKey.TaxCapitalGains

class FederalTax implements ITaxCalculator<TaxVarietyWithFilingStatus> {
  public readonly metadata: TaxMetadata
  private readonly bracketsByYearAndFilingStatus: Map<
    string,
    TaxFederalBracket[]
  >

  constructor(
    taxKey: FederalTaxKey,
    private readonly maxPredictionsYear: number
  ) {
    this.metadata = TAX_BY_KEY.get(taxKey)!
    this.bracketsByYearAndFilingStatus = new Map()
    const source =
      taxKey === TaxKey.TaxCapitalGains
        ? CAPITAL_GAINS_BRACKETS
        : FEDERAL_INCOME_BRACKETS
    this.initializeBrackets(source)
    this.predictBrackets(source)
  }

  private initializeBrackets(source: readonly TaxFederalBracket[]): void {
    source.forEach(bracket => {
      const key = `${bracket.year}-${bracket.filingStatus}`
      if (!this.bracketsByYearAndFilingStatus.has(key)) {
        this.bracketsByYearAndFilingStatus.set(key, [])
      }
      this.bracketsByYearAndFilingStatus.get(key)!.push(bracket)
    })

    // Sort brackets by 'to' amount for each year/filing status combination
    this.bracketsByYearAndFilingStatus.forEach(brackets => {
      brackets.sort((a, b) => a.to - b.to)
    })
  }

  private predictBrackets(source: readonly TaxFederalBracket[]): void {
    // Group brackets by filing status for prediction
    const bracketsByFilingStatus = new Map<FilingStatus, TaxFederalBracket[]>()

    source.forEach(bracket => {
      if (!bracketsByFilingStatus.has(bracket.filingStatus)) {
        bracketsByFilingStatus.set(bracket.filingStatus, [])
      }
      bracketsByFilingStatus.get(bracket.filingStatus)!.push(bracket)
    })

    bracketsByFilingStatus.forEach((brackets, filingStatus) => {
      // Find the latest year for this filing status
      let lastYear = 0
      let lastYearBrackets: TaxFederalBracket[] = []

      brackets.forEach(bracket => {
        if (bracket.year > lastYear) {
          lastYear = bracket.year
          lastYearBrackets = brackets.filter(b => b.year === bracket.year)
        }
      })

      if (lastYear > this.maxPredictionsYear) {
        return
      }

      // Predict brackets for future years
      for (let year = lastYear + 1; year <= this.maxPredictionsYear; year++) {
        const predictedBrackets = lastYearBrackets.map(bracket => ({
          ...bracket,
          year,
          to: projectValue(
            bracket.to,
            this.metadata.growthRate,
            bracket.year,
            year
          ),
        }))

        // Add predicted brackets to our map
        const key = `${year}-${filingStatus}`
        // Sort predicted brackets by 'to' amount
        predictedBrackets.sort((a, b) => a.to - b.to)
        this.bracketsByYearAndFilingStatus.set(key, predictedBrackets)
      }
    })
  }

  public getTaxRate(year: number, variety: TaxVarietyWithFilingStatus): number {
    const brackets = this.getBracketsForYearAndFilingStatus(
      year,
      variety.filingStatus
    )
    if (brackets.length === 0) {
      return 0
    }

    // Return the highest tax rate as a rough estimate
    return Math.max(...brackets.map(bracket => bracket.taxRate))
  }

  public getTaxableThreshold(
    year: number,
    variety: TaxVarietyWithFilingStatus
  ): number {
    const brackets = this.getBracketsForYearAndFilingStatus(
      year,
      variety.filingStatus
    )
    if (brackets.length === 0) {
      return Infinity
    }

    return Math.max(...brackets.map(bracket => bracket.to))
  }

  public calculate(
    amount: number,
    year: number,
    variety: TaxVarietyWithFilingStatus
  ): TaxCalculationResult {
    const brackets = this.getBracketsForYearAndFilingStatus(
      year,
      variety.filingStatus
    )

    if (brackets.length === 0) {
      return this.createEmptyResult(amount)
    }

    return this.calculateTaxWithBrackets(
      amount,
      brackets,
      year,
      variety.filingStatus
    )
  }

  private getBracketsForYearAndFilingStatus(
    year: number,
    filingStatus: FilingStatus
  ): TaxFederalBracket[] {
    const key = `${year}-${filingStatus}`
    return this.bracketsByYearAndFilingStatus.get(key) || []
  }

  private calculateTaxWithBrackets(
    amount: number,
    brackets: TaxFederalBracket[],
    year: number,
    filingStatus: FilingStatus
  ): TaxCalculationResult {
    let totalTax = 0
    let remainingIncome = amount
    const bracketBreakdown: TaxBracketBreakdown[] = []

    for (let i = 0; i < brackets.length; i++) {
      const bracket = brackets[i]
      const bracketFrom = this.calculateBracketFromValue(brackets, i)
      const bracketUpperLimit = bracket.to

      // Calculate how much income falls into this bracket
      const incomeInBracket = Math.max(
        0,
        Math.min(remainingIncome, bracketUpperLimit - bracketFrom)
      )

      if (incomeInBracket > 0) {
        // Calculate tax for this bracket
        const taxInBracket = incomeInBracket * bracket.taxRate
        totalTax += taxInBracket

        bracketBreakdown.push({
          year,
          filingStatus,
          from: bracketFrom,
          to: bracketUpperLimit,
          taxRate: bracket.taxRate,
          description: `${this.metadata.name} bracket ${i + 1}`,
          incomeInBracket,
          taxInBracket,
        })

        remainingIncome -= incomeInBracket
      }

      // If no more income to tax, break
      if (remainingIncome <= 0) {
        break
      }
    }

    // Calculate effective tax rate
    const effectiveRate = amount > 0 ? totalTax / amount : 0

    return {
      amount,
      taxMetadata: this.metadata,
      taxableIncome: amount,
      standardDeduction: 0, // Federal tax calculation doesn't include standard deduction
      effectiveRate,
      totalTax,
      bracketBreakdown,
    }
  }

  private calculateBracketFromValue(
    brackets: TaxFederalBracket[],
    index: number
  ): number {
    return index === 0 ? 0 : brackets[index - 1].to
  }

  private createEmptyResult(amount: number): TaxCalculationResult {
    return {
      amount,
      taxMetadata: this.metadata,
      taxableIncome: amount,
      standardDeduction: 0,
      effectiveRate: 0,
      totalTax: 0,
      bracketBreakdown: [],
    }
  }
}

export default FederalTax
